import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import { getMctRisk } from "../../utils/mctUtils";
import { AIRPORTS } from "../FlightRouteMap";
import { calcConnTime, toMins, fmtConn } from "../../utils/timeUtils";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { AirlineLogo } from "../shared";
import {
  PROVIDERS,
  flightToIcaoCallsign, paddedCallsigns, regionalCallsigns,
  fetchFromProvider, fetchFromProxy, lookupCallsign,
} from "../../utils/flightTrackingUtils";
import { getCachedIcao24, setCachedIcao24 } from "../../utils/icao24Cache";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RISK_COLORS = {
  AT_RISK: "#D46060",
  TIGHT:   "#c8a830",
  SAFE:    "#6EC472",
  null:    "#7A84A0",
};

const RISK_LABELS = {
  AT_RISK: "AT RISK",
  TIGHT:   "TIGHT",
  SAFE:    "SAFE",
};

const RISK_ORDER = { AT_RISK: 0, TIGHT: 1, SAFE: 2, null: 3 };

// ---------------------------------------------------------------------------
// Mission Control theme tokens
// ---------------------------------------------------------------------------

const MC_DARK = {
  pageBg:          "#060912",
  panelBg:         "rgba(6,9,18,0.82)",
  cardBg:          "linear-gradient(135deg, rgba(30,38,65,0.55) 0%, rgba(16,22,44,0.65) 100%)",
  cardBgHoverBase: "rgba(20,30,60,0.62)",
  cardBgSelected:  "linear-gradient(135deg, rgba(74,124,247,0.28) 0%, rgba(20,30,60,0.65) 100%)",
  tooltipBg:       "rgba(6,9,18,0.92)",
  tooltipBgEst:    "rgba(6,9,18,0.85)",
  btnBg:           "rgba(255,255,255,0.07)",
  pillBg:          "rgba(255,255,255,0.05)",
  connBg:          "rgba(255,255,255,0.07)",
  insetShadow:     "inset 0 1px 0 rgba(255,255,255,0.12)",
  border:          "rgba(255,255,255,0.10)",
  borderBtn:       "rgba(255,255,255,0.15)",
  divider:         "rgba(255,255,255,0.15)",
  cardDivider:     "rgba(255,255,255,0.06)",
  pillBorder:      "rgba(255,255,255,0.07)",
  connBorder:      "rgba(255,255,255,0.12)",
  separator:       "rgba(255,255,255,0.12)",
  separatorDot:    "rgba(255,255,255,0.18)",
  textPrimary:     "#dde4f8",
  textSecondary:   "#aab8d4",
  textMuted:       "#7A8AAC",
  textDim:         "#4A5270",
  textEmpty:       "#4A5068",
  textLabel:       "#6070A0",
  textValue:       "#8898bc",
  textEst:         "#3A4260",
  dotBorder:       "#060912",
  originDot:       "#9aaac8",
  originLabel:     "#c8d4ec",
  laxLabel:        "#4A7CF7",
  labelShadow:     "0 1px 4px #000,0 0 8px #000",
  tooltipMuted:    "#5A6480",
  tooltipStatus:   "#a0aac0",
  tileset:         "dark_nolabels",
  shadowCard:      "rgba(0,0,0,0.30)",
  shadowSelected:  "rgba(0,0,0,0.40)",
  shadowPanel:     "0 4px 24px rgba(0,0,0,0.6)",
  shadowTooltip:   "0 2px 14px rgba(0,0,0,0.7)",
};

const MC_LIGHT = {
  pageBg:          "#b8c8d8",
  panelBg:         "rgba(172,192,215,0.92)",
  cardBg:          "linear-gradient(135deg, rgba(195,212,230,0.82) 0%, rgba(178,198,220,0.88) 100%)",
  cardBgHoverBase: "rgba(195,210,230,0.82)",
  cardBgSelected:  "linear-gradient(135deg, rgba(74,124,247,0.18) 0%, rgba(185,205,228,0.88) 100%)",
  tooltipBg:       "rgba(195,212,230,0.97)",
  tooltipBgEst:    "rgba(195,212,230,0.92)",
  btnBg:           "rgba(50,80,130,0.12)",
  pillBg:          "rgba(50,80,130,0.07)",
  connBg:          "rgba(50,80,130,0.10)",
  insetShadow:     "inset 0 1px 0 rgba(255,255,255,0.50)",
  border:          "rgba(60,90,140,0.20)",
  borderBtn:       "rgba(60,90,140,0.28)",
  divider:         "rgba(60,90,140,0.22)",
  cardDivider:     "rgba(60,90,140,0.10)",
  pillBorder:      "rgba(60,90,140,0.15)",
  connBorder:      "rgba(60,90,140,0.18)",
  separator:       "rgba(60,90,140,0.18)",
  separatorDot:    "rgba(60,90,140,0.30)",
  textPrimary:     "#18253d",
  textSecondary:   "#2a3a5a",
  textMuted:       "#4a5e7a",
  textDim:         "#6a7a90",
  textEmpty:       "#6a7a90",
  textLabel:       "#5a6880",
  textValue:       "#334468",
  textEst:         "#8898aa",
  dotBorder:       "#aabcce",
  originDot:       "#3a5880",
  originLabel:     "#1e3060",
  laxLabel:        "#2255cc",
  labelShadow:     "0 1px 3px rgba(160,185,210,0.9),0 0 6px rgba(160,185,210,0.7)",
  tooltipMuted:    "#5a6880",
  tooltipStatus:   "#4a5870",
  tileset:         "light_nolabels",
  shadowCard:      "rgba(50,80,130,0.12)",
  shadowSelected:  "rgba(50,80,130,0.20)",
  shadowPanel:     "0 4px 24px rgba(50,80,130,0.20)",
  shadowTooltip:   "0 2px 14px rgba(50,80,130,0.25)",
};


const LAX = [33.9425, -118.4081];
const LAX_RADIUS_KM = 50; // within this distance = at LAX

function distKm(lat1, lon1, lat2, lon2) {
  const R = 6371, toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Bearing in degrees (0–360) from point 1 → point 2
function bearingDeg(lat1, lon1, lat2, lon2) {
  const toRad = d => d * Math.PI / 180;
  const φ1 = toRad(lat1), φ2 = toRad(lat2), Δλ = toRad(lon2 - lon1);
  const x = Math.sin(Δλ) * Math.cos(φ2);
  const y = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(x, y) * 180 / Math.PI + 360) % 360;
}

// Estimate aircraft position along great circle from origin to LAX based on ETA/STA.
// Uses a three-phase speed profile (climb / cruise / descent) for more accurate placement.
// Returns { coords, arc, fractionComplete } or null if position can't be estimated.
function estimatePosition(entry, nowDate) {
  const origin = (entry.origin || "").trim().toUpperCase();
  const originCoords = AIRPORTS[origin];
  if (!originCoords) return null;

  // STA/ETA is block-in (gate) time; subtract taxi time to get runway touchdown
  const TAXI_MINS = 7;
  const arrMins = toMins(entry.eta || entry.sta);
  if (arrMins === null) return null;
  const touchdownMins = arrMins - TAXI_MINS;

  const nowMins = nowDate.getHours() * 60 + nowDate.getMinutes();
  let minsToArr = touchdownMins - nowMins;
  if (minsToArr < -600) minsToArr += 1440; // next-day wrap
  if (minsToArr < 0) return null; // already landed

  const toRad = d => d * Math.PI / 180;
  const [lat1, lon1] = originCoords;
  const dLat = toRad(LAX[0] - lat1);
  const dLon = toRad(LAX[1] - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(LAX[0])) * Math.sin(dLon/2)**2;
  const distKm = 2 * 6371 * Math.asin(Math.sqrt(a));

  // Three-phase speed profile: climb (250kt restriction + acceleration), cruise, descent
  const kph = kts => kts * 1.852;
  const CLIMB_MINS = 20,   CLIMB_KTS = 300;   // avg incl. 250kt restriction below 10k ft
  const DESCENT_MINS = 25, DESCENT_KTS = 280;  // avg incl. approach deceleration
  const CRUISE_KTS = 480;

  let climbDistKm   = kph(CLIMB_KTS)   * (CLIMB_MINS   / 60);  // ~185 km
  let descentDistKm = kph(DESCENT_KTS) * (DESCENT_MINS / 60);  // ~216 km
  let climbMins = CLIMB_MINS, descentMins = DESCENT_MINS;

  // For short flights, scale climb/descent proportionally so they fit within total distance
  const phaseDist = climbDistKm + descentDistKm;
  if (phaseDist > distKm) {
    const scale = distKm / phaseDist;
    climbDistKm   *= scale;
    descentDistKm *= scale;
    climbMins   = climbDistKm   / (kph(CLIMB_KTS)   / 60);
    descentMins = descentDistKm / (kph(DESCENT_KTS) / 60);
  }

  const cruiseDistKm = distKm - climbDistKm - descentDistKm;
  const cruiseMins   = cruiseDistKm / (kph(CRUISE_KTS) / 60);
  const totalMins    = climbMins + cruiseMins + descentMins;

  const minsElapsed = totalMins - minsToArr;
  if (minsElapsed <= 0) return null; // hasn't departed yet

  // Distance covered based on which phase the flight is currently in
  let distCovered;
  if (minsElapsed <= climbMins) {
    distCovered = (minsElapsed / climbMins) * climbDistKm;
  } else if (minsElapsed <= climbMins + cruiseMins) {
    distCovered = climbDistKm + (minsElapsed - climbMins) * (kph(CRUISE_KTS) / 60);
  } else {
    distCovered = climbDistKm + cruiseDistKm + (minsElapsed - climbMins - cruiseMins) * (kph(DESCENT_KTS) / 60);
  }

  const fractionComplete = Math.max(0, Math.min(1, distCovered / distKm));

  const arc = greatCirclePoints(lat1, lon1, LAX[0], LAX[1], 100);
  const idx = Math.round(fractionComplete * (arc.length - 1));
  return { coords: arc[Math.min(idx, arc.length - 1)], arc, fractionComplete };
}

// Adds perpendicular offsets to alternating midpoints of an arc to produce a zigzag path.
function zigzagPoints(arcPts, amplitude = 1.8) {
  if (arcPts.length < 2) return arcPts;
  const out = [];
  for (let i = 0; i < arcPts.length - 1; i++) {
    const [lat1, lon1] = arcPts[i];
    const [lat2, lon2] = arcPts[i + 1];
    out.push([lat1, lon1]);
    const midLat = (lat1 + lat2) / 2;
    const midLon = (lon1 + lon2) / 2;
    const dLat = lat2 - lat1, dLon = lon2 - lon1;
    const len = Math.sqrt(dLat * dLat + dLon * dLon) || 1;
    const side = (i % 2 === 0) ? 1 : -1;
    out.push([midLat + side * amplitude * (-dLon / len), midLon + side * amplitude * (dLat / len)]);
  }
  out.push(arcPts[arcPts.length - 1]);
  return out;
}

// Returns { pos, bothVisible } for an arc label.
// pos    — arc point at the midpoint of the currently-visible portion (tracks on-screen).
// bothVisible — true when both the arc's start (LAX) and end (dest) are inside the viewport.
// Uses latLngToContainerPoint so unwrapped longitudes (>180° / <-180°) are handled correctly.
function getArcLabelState(arc, map) {
  const size = map.getSize();
  const inView = pt => { const p = map.latLngToContainerPoint(pt); return p.x >= 0 && p.x <= size.x && p.y >= 0 && p.y <= size.y; };
  const visible = [];
  for (let i = 0; i < arc.length; i++) {
    if (inView(arc[i])) visible.push(i);
  }
  return {
    pos: visible.length > 0 ? arc[visible[Math.floor(visible.length / 2)]] : arc[Math.floor(arc.length / 2)],
    bothVisible: inView(arc[0]) && inView(arc[arc.length - 1]),
  };
}

function greatCirclePoints(lat1, lon1, lat2, lon2, n = 60) {
  const R = Math.PI / 180;
  const φ1 = lat1*R, λ1 = lon1*R, φ2 = lat2*R, λ2 = lon2*R;
  const d = 2 * Math.asin(Math.sqrt(
    Math.sin((φ2-φ1)/2)**2 + Math.cos(φ1)*Math.cos(φ2)*Math.sin((λ2-λ1)/2)**2
  ));
  if (d === 0) return [[lat1, lon1]];
  const raw = Array.from({ length: n + 1 }, (_, i) => {
    const f = i / n;
    const A = Math.sin((1-f)*d) / Math.sin(d);
    const B = Math.sin(f*d)     / Math.sin(d);
    const x = A*Math.cos(φ1)*Math.cos(λ1) + B*Math.cos(φ2)*Math.cos(λ2);
    const y = A*Math.cos(φ1)*Math.sin(λ1) + B*Math.cos(φ2)*Math.sin(λ2);
    const z = A*Math.sin(φ1)               + B*Math.sin(φ2);
    return [Math.atan2(z, Math.sqrt(x*x + y*y)) / R, Math.atan2(y, x) / R];
  });
  for (let i = 1; i < raw.length; i++) {
    let lon = raw[i][1];
    const prev = raw[i - 1][1];
    while (lon - prev >  180) lon -= 360;
    while (lon - prev < -180) lon += 360;
    raw[i] = [raw[i][0], lon];
  }
  return raw;
}

function fmtTime(t) {
  if (!t) return "--:--";
  return t.replace(":", "").replace(/(\d{2})(\d{2})/, "$1:$2");
}

// ---------------------------------------------------------------------------
// Direct DOM opacity helper for divIcon markers — Leaflet's setOpacity is unreliable for divIcon.
function setOriginOpacity(marker, val) {
  const el = marker.getElement ? marker.getElement() : null;
  if (el) { el.style.opacity = val; return; }
  marker.setOpacity(val); // fallback if not yet in DOM
}

// Tooltip deconfliction — greedy direction assignment to avoid label overlap
// ---------------------------------------------------------------------------

// 8 placement slots: 4 cardinal + 4 diagonal (diagonal uses an x-offset on top/bottom).
// Each slot defines the Leaflet direction and the pixel offset to apply.
const TOOLTIP_SLOTS = [
  { direction: "top",    offset: [  0, -16] },  // top
  { direction: "bottom", offset: [  0,  16] },  // bottom
  { direction: "right",  offset: [ 12,   0] },  // right
  { direction: "left",   offset: [-12,   0] },  // left
  { direction: "top",    offset: [ 52, -16] },  // top-right
  { direction: "top",    offset: [-52, -16] },  // top-left
  { direction: "bottom", offset: [ 52,  16] },  // bottom-right
  { direction: "bottom", offset: [-52,  16] },  // bottom-left
];

// Returns the approximate screen centre of a tooltip box for a given slot index.
function tooltipCenter(pt, slotIdx) {
  const { direction, offset } = TOOLTIP_SLOTS[slotIdx];
  const bx = pt.x + offset[0];
  const by = pt.y + offset[1];
  switch (direction) {
    case "right":  return [bx + 75, by];
    case "bottom": return [bx,      by + 36];
    case "left":   return [bx - 75, by];
    default:       return [bx,      by - 36]; // "top"
  }
}

// After all flight markers are (re-)placed, pick the slot for each permanent
// tooltip that minimises collisions with already-assigned neighbours.
// Each entry in markersRef must carry { tooltipHtml, tooltipOpts } for this to work.
function deconflictTooltips(map, markersRef) {
  const OVERLAP_RADIUS = 130; // px — two label centres closer than this are "overlapping"

  const items = Object.values(markersRef.current)
    .filter(({ marker, tooltipHtml }) => marker && tooltipHtml)
    .map(({ marker, tooltipHtml, tooltipOpts }) => ({
      marker, tooltipHtml, tooltipOpts,
      pt: map.latLngToContainerPoint(marker.getLatLng()),
      slotIdx: 0,
    }));

  // Greedy: for each marker, pick the slot whose label centre is furthest
  // from all already-assigned label centres
  for (let i = 0; i < items.length; i++) {
    const { pt } = items[i];
    let bestSlot = 0, bestScore = Infinity;
    for (let s = 0; s < TOOLTIP_SLOTS.length; s++) {
      const c = tooltipCenter(pt, s);
      let score = 0;
      for (let j = 0; j < i; j++) {
        const pc = tooltipCenter(items[j].pt, items[j].slotIdx);
        if (Math.hypot(c[0] - pc[0], c[1] - pc[1]) < OVERLAP_RADIUS) score++;
      }
      if (score < bestScore) { bestScore = score; bestSlot = s; }
      if (bestScore === 0) break;
    }
    items[i].slotIdx = bestSlot;
  }

  // Re-bind only the tooltips whose slot changed
  for (const { marker, tooltipHtml, tooltipOpts, slotIdx } of items) {
    const cur = marker.getTooltip?.();
    if (!cur) continue;
    const { direction, offset } = TOOLTIP_SLOTS[slotIdx];
    if (cur.options.direction === direction &&
        cur.options.offset?.[0] === offset[0] &&
        cur.options.offset?.[1] === offset[1]) continue;
    marker.unbindTooltip();
    marker.bindTooltip(tooltipHtml, { ...tooltipOpts, direction, offset });
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatPill({ count, label, color }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: color + "22", border: `1px solid ${color}55`,
      borderRadius: 20, padding: "3px 10px",
    }}>
      <span style={{ color, fontWeight: 700, fontSize: 14, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
        {count}
      </span>
      <span style={{ color: color + "cc", fontWeight: 600, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {label}
      </span>
    </div>
  );
}

function TopBar({ now, atRisk, tight, safe, skyError, skyAge, skyProviderIdx, onClose, t }) {
  const timeStr = now.toLocaleTimeString("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  const dateStr = now.toLocaleDateString("en-US", {
    timeZone: "America/Los_Angeles",
    weekday: "short", month: "short", day: "numeric",
  });

  const skyAlive    = !skyError && skyAge !== null && (Date.now() - skyAge) < 150000;
  const providerLabel = PROVIDERS[skyProviderIdx]?.label ?? "OpenSky";
  const skyStatus   = skyAlive
    ? (skyProviderIdx === 0 ? "LIVE" : `LIVE · ${providerLabel}`)
    : "OFFLINE";
  const skyColor    = skyAlive ? "#6EC472" : "#D46060";

  return (
    <div style={{
      position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
      zIndex: 10, display: "flex", alignItems: "center", gap: 12,
      background: t.panelBg, backdropFilter: "blur(12px)",
      border: `1px solid ${t.border}`, borderRadius: 32,
      padding: "8px 16px", boxShadow: t.shadowPanel,
      whiteSpace: "nowrap", zoom: 1.1,
    }}>
      {/* Radar icon */}
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: "#4A7CF7" }}>
        <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M8 8 L8 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <circle cx="8" cy="8" r="1.2" fill="currentColor"/>
        <path d="M5 4.5 L2.5 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M11 4.5 L13.5 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>

      <span style={{ color: t.textPrimary, fontWeight: 700, fontSize: 13, letterSpacing: "0.12em", textTransform: "uppercase" }}>
        Mission Control
      </span>

      <div style={{ width: 1, height: 20, background: t.divider }} />

      <div style={{ textAlign: "center" }}>
        <div style={{ color: t.textPrimary, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: "0.05em" }}>
          {timeStr}
        </div>
        <div style={{ color: t.textMuted, fontSize: 10, letterSpacing: "0.06em" }}>
          {dateStr} LAX
        </div>
      </div>

      <div style={{ width: 1, height: 20, background: t.divider }} />

      <div style={{ display: "flex", gap: 6 }}>
        <StatPill count={atRisk} label="At Risk" color={RISK_COLORS.AT_RISK} />
        <StatPill count={tight}  label="Tight"   color={RISK_COLORS.TIGHT}   />
        <StatPill count={safe}   label="Safe"    color={RISK_COLORS.SAFE}    />
      </div>

      <div style={{ width: 1, height: 20, background: t.divider }} />

      {/* OpenSky status */}
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <div style={{
          width: 7, height: 7, borderRadius: "50%",
          background: skyColor,
          boxShadow: skyAlive ? `0 0 6px ${skyColor}` : "none",
        }} />
        <span style={{ color: skyColor, fontSize: 10, fontWeight: 700, letterSpacing: "0.1em" }}>
          {skyStatus}
        </span>
      </div>

      <div style={{ width: 1, height: 20, background: t.divider }} />

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          background: t.btnBg, border: `1px solid ${t.borderBtn}`,
          borderRadius: "50%", width: 28, height: 28, color: t.textPrimary, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
        title="Close Mission Control"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}

function ConnectionCard({ entry, now, isSelected, isHovered, isDimmed, onClick, onMouseEnter, onMouseLeave, cardRef, isLive = false, hasEst = false, t }) {
  const risk  = entry.risk;
  const color = RISK_COLORS[risk] || RISK_COLORS[null];
  const ACCENT = "#5B8EFF";

  const nowMins = now.getHours() * 60 + now.getMinutes();
  const outMins = toMins(entry.atd || entry.etd || entry.std);
  let depsIn = outMins !== null ? outMins - nowMins : null;
  if (depsIn !== null && depsIn < -600) depsIn += 1440;

  const depStr = depsIn === null ? "--"
    : depsIn < 0   ? `${Math.abs(depsIn)}m ago`
    : depsIn === 0 ? "NOW"
    : depsIn < 60  ? `${depsIn}m`
    : `${Math.floor(depsIn/60)}h ${depsIn % 60}m`;

  const connStr  = entry.connMins !== null ? (fmtConn(entry.connMins) ?? "--") : "--";
  const isUrgent = depsIn !== null && depsIn < 30;
  const pax      = entry.ttlPax || null;

  const inAirline  = (entry.inAirline  || (entry.inFlt  || "").slice(0, 2)).toUpperCase();
  const outAirline = (entry.outAirline || (entry.outFlt || "").slice(0, 2)).toUpperCase();

  const arrTime  = fmtTime(entry.ata || entry.eta || entry.sta);
  const arrLabel = entry.ata ? "ATA" : entry.eta ? "ETA" : "STA";
  const depTime  = fmtTime(entry.atd || entry.etd || entry.std);
  const depLabel = entry.atd ? "ATD" : entry.etd ? "ETD" : "STD";

  const gatePill = (gate) => gate ? (
    <span style={{
      color: ACCENT, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: 9,
      border: `1px solid ${ACCENT}66`, borderRadius: 10,
      padding: "0px 5px", lineHeight: 1.6, flexShrink: 0,
    }}>{gate}</span>
  ) : null;

  return (
    <div
      ref={cardRef}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "relative",
        marginBottom: 8,
        borderRadius: 14,
        cursor: "pointer",
        opacity: isDimmed ? 0.28 : 1,
        boxShadow: (isSelected || isHovered)
          ? `0 0 0 1.5px ${color}88, -3px 0 14px -2px ${color}66, 0 6px 20px ${t.shadowSelected}`
          : risk
          ? `-3px 0 12px -2px ${color}33, 0 4px 16px ${t.shadowCard}`
          : `0 4px 16px ${t.shadowCard}`,
        transition: "box-shadow 0.15s, opacity 0.15s",
      }}
    >
      <div style={{
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        background: isSelected
          ? t.cardBgSelected
          : isHovered
          ? `linear-gradient(135deg, ${color}22 0%, ${t.cardBgHoverBase} 100%)`
          : t.cardBg,
        border: `1px solid ${isSelected ? "rgba(74,124,247,0.35)" : isHovered ? `${color}44` : t.border}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 14,
        padding: "11px 12px 10px",
        boxShadow: t.insetShadow,
        transition: "background 0.15s, border-color 0.15s",
      }}>

        {/* INBOUND + SEPARATOR + OUTBOUND — horizontal, matching main dashboard layout */}
        <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 10 }}>

          {/* Inbound */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <AirlineLogo iata={inAirline} size={36} />
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: 16,
                color: t.textPrimary, fontWeight: 700, letterSpacing: "0.02em",
              }}>
                {entry.inFlt || "???"}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                {gatePill(entry.inGate)}
                <span style={{
                  color: t.textMuted, fontSize: 9, textTransform: "uppercase",
                  letterSpacing: "0.04em", whiteSpace: "nowrap",
                  overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {(entry.origin || "").trim().toUpperCase() || "—"} › {arrTime} {arrLabel}
                </span>
              </div>
            </div>
          </div>

          {/* Centre separator */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, flexShrink: 0, padding: "0 10px" }}>
            <div style={{ width: 24, height: 1, background: t.separator }} />
            <span style={{ color: t.separatorDot, fontSize: 8 }}>✦</span>
          </div>

          {/* Outbound */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <AirlineLogo iata={outAirline} size={36} />
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: 16,
                  color: entry.isCancelled ? "#D46060" : t.textSecondary, fontWeight: 700, letterSpacing: "0.02em",
                }}>
                  {entry.outFlt || "???"}
                </span>
                {entry.isCancelled && (
                  <span style={{
                    background: "#D4606020", color: "#D46060", border: "1px solid #D4606044",
                    borderRadius: 6, padding: "1px 5px",
                    fontSize: 8, fontWeight: 700, letterSpacing: "0.13em",
                    textTransform: "uppercase", flexShrink: 0,
                  }}>CNCL</span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                {gatePill(entry.outGate)}
                <span style={{
                  color: t.textMuted, fontSize: 9, textTransform: "uppercase",
                  letterSpacing: "0.04em", whiteSpace: "nowrap",
                  overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {(entry.dest || "").toUpperCase() || "LAX"} › {depTime} {depLabel}
                </span>
              </div>
            </div>
          </div>

        </div>

        {/* FOOTER: risk · MCT · dep · PAX · live */}
        <div style={{
          display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap",
          borderTop: `1px solid ${t.cardDivider}`, paddingTop: 8,
        }}>
          {risk && !entry.isCancelled && (
            <span style={{
              background: color + "1E", color, border: `1px solid ${color}44`,
              borderRadius: 8, padding: "2px 6px",
              fontSize: 8, fontWeight: 700, letterSpacing: "0.13em",
              textTransform: "uppercase", flexShrink: 0,
            }}>
              {RISK_LABELS[risk]}
            </span>
          )}
          <div style={{
            display: "flex", alignItems: "center", gap: 4,
            background: t.connBg, border: `1px solid ${t.connBorder}`,
            borderRadius: 7, padding: "2px 7px",
          }}>
            <span style={{ color: t.textLabel, fontSize: 8, textTransform: "uppercase", letterSpacing: "0.09em" }}>CONN</span>
            <span style={{ color, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 700, fontSize: 10.5 }}>{connStr}</span>
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: 4,
            background: isUrgent ? "rgba(200,168,48,0.08)" : t.pillBg,
            border: `1px solid ${isUrgent ? "rgba(200,168,48,0.25)" : t.pillBorder}`,
            borderRadius: 7, padding: "2px 7px",
          }}>
            <span style={{ color: t.textLabel, fontSize: 8, textTransform: "uppercase", letterSpacing: "0.09em" }}>Dep</span>
            <span style={{
              color: isUrgent ? "#c8a830" : t.textValue,
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: 10,
              fontWeight: isUrgent ? 700 : 500,
            }}>{depStr}</span>
          </div>
          {pax !== null && (
            <div style={{
              display: "flex", alignItems: "center", gap: 4,
              background: t.pillBg, border: `1px solid ${t.pillBorder}`,
              borderRadius: 7, padding: "2px 7px",
            }}>
              <span style={{ color: t.textLabel, fontSize: 8, textTransform: "uppercase", letterSpacing: "0.09em" }}>PAX</span>
              <span style={{ color: t.textValue, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: 10, fontWeight: 600 }}>{pax}</span>
            </div>
          )}
          <div style={{ marginLeft: "auto", flexShrink: 0 }}>
            {isLive ? (
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#6EC472", boxShadow: "0 0 5px #6EC472", display: "inline-block" }} />
                <span style={{ color: "#6EC472", fontSize: 8, fontWeight: 700, letterSpacing: "0.12em" }}>LIVE</span>
              </span>
            ) : hasEst ? (
              <span style={{ color: t.textEst, fontSize: 8, fontWeight: 600, letterSpacing: "0.09em" }}>~ EST</span>
            ) : null}
          </div>
        </div>

      </div>
    </div>
  );
}

function LeftPanel({ entries, now, selectedInFlt, hoveredInFlt, onSelect, onCardEnter, onCardLeave, liveIds, estIds, t }) {
  const scrollRef  = useRef(null);
  const cardRefs   = useRef({});  // inFlt → DOM node

  // Scroll the first matching card into view when selection or hover changes via the map
  useEffect(() => {
    const activeInFlt = selectedInFlt || hoveredInFlt;
    if (!activeInFlt) return;
    // Find the first entry with this inFlt and scroll to its card
    const firstMatch = entries.find(e => e.inFlt === activeInFlt);
    if (!firstMatch) return;
    const node = cardRefs.current[firstMatch.id];
    if (node && scrollRef.current) {
      node.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedInFlt, hoveredInFlt, entries]);

  return (
    <div ref={scrollRef} style={{
      position: "absolute", top: 80, left: 16, bottom: 16, width: 390,
      zIndex: 10, overflowY: "auto", paddingRight: 4, zoom: 1.1,
    }}>
      <div style={{
        color: t.textDim, fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase",
        fontWeight: 700, marginBottom: 10, paddingLeft: 3,
        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
      }}>
        Active Connections ({entries.length})
      </div>
      {entries.length === 0 ? (
        <div style={{ color: t.textEmpty, fontSize: 11, fontStyle: "italic", padding: "8px 4px" }}>
          No active connections
        </div>
      ) : (
        entries.map(e => {
          const activeInFlt = selectedInFlt || hoveredInFlt;
          const isActive = e.inFlt === activeInFlt;
          return (
          <ConnectionCard
            key={e.id}
            entry={e}
            now={now}
            isSelected={selectedInFlt === e.inFlt}
            isHovered={hoveredInFlt === e.inFlt}
            isDimmed={!!activeInFlt && !isActive}
            onClick={() => onSelect(e.inFlt === selectedInFlt ? null : e.inFlt)}
            onMouseEnter={() => onCardEnter(e)}
            onMouseLeave={() => onCardLeave(e)}
            cardRef={el => { cardRefs.current[e.id] = el; }}
            isLive={liveIds.has(e.id)}
            hasEst={estIds.has(e.id)}
            t={t}
          />
          );
        })
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function WarRoomModal({ entries, onClose, isDark = true }) {
  useLockBodyScroll();
  const t = isDark ? MC_DARK : MC_LIGHT;
  const isDarkRef = useRef(isDark);
  useEffect(() => { isDarkRef.current = isDark; }, [isDark]);

  const mapContainerRef = useRef(null);
  const mapInstanceRef  = useRef(null);
  const markersRef      = useRef({});   // key: entry.id → { marker, line }
  const originMarkersRef = useRef({});  // key: IATA code → { dotMarker, labelMarker, inFlts }
  const landedAtRef      = useRef({});  // key: inFlt → timestamp when first seen on ground at LAX
  const routeLinesRef   = useRef({});
  const selectedArcRef  = useRef(null);
  const arcLabelsRef       = useRef([]);         // { marker, arc }[] — repositioned on zoom/pan
  const nowRef             = useRef(new Date());
  const icao24AttemptedRef = useRef(new Set());  // flights with AviationStack call attempted this session

  const [aircraft,       setAircraft]       = useState([]);
  // Maps inFlt → resolved ADS-B callsign (e.g. "LO21" → "LOT2CA") discovered via icao24 lookup.
  // Injected into callsignAlts so the main poll finds the aircraft by callsign from then on.
  const [resolvedCallsigns, setResolvedCallsigns] = useState(() => {
    // Pre-populate from today's cache on mount so resolved callsigns survive re-opens
    const result = {};
    try {
      const raw = JSON.parse(localStorage.getItem("sa_lax_icao24_cache") || "{}");
      const today = new Date().toISOString().slice(0, 10);
      for (const [key, val] of Object.entries(raw)) {
        const [flt, date] = key.split("|");
        if (date === today && val?.resolvedCallsign) result[flt] = val.resolvedCallsign;
      }
    } catch {}
    return result;
  });
  const [landedEntryIds, setLandedEntryIds] = useState(new Set()); // sticky: entry IDs whose inbound has landed at LAX
  const [skyError,       setSkyError]       = useState(false);
  const [skyProviderIdx, setSkyProviderIdx] = useState(0);   // index into PROVIDERS
  const [skyAge,         setSkyAge]         = useState(null);
  const providerCooldownRef = useRef({});  // { providerKey: timestampMs }
  const [now,         setNow]         = useState(() => new Date());
  const [selectedInFlt, setSelectedInFlt] = useState(null);
  const selectedInFltRef = useRef(null); // mirror for use inside event-handler closures
  const [hoveredInFlt,  setHoveredInFlt]  = useState(null);
  const [panelVisible,  setPanelVisible]  = useState(true);
  const [laxSelected,   setLaxSelected]   = useState(false);
  const laxSelectedRef  = useRef(false);
  const laxMarkerRef    = useRef(null);

  // Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // 1-second clock
  useEffect(() => {
    const t = setInterval(() => {
      const n = new Date();
      setNow(n);
      nowRef.current = n;
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Keep refs in sync so event-handler closures always see current value
  useEffect(() => { selectedInFltRef.current = selectedInFlt; }, [selectedInFlt]);
  useEffect(() => { laxSelectedRef.current = laxSelected; }, [laxSelected]);

  // Update LAX marker visual when selected/deselected
  useEffect(() => {
    const m = laxMarkerRef.current;
    if (!m) return;
    const L = require("leaflet");
    m.setIcon(L.divIcon({
      className: "",
      html: laxSelected
        ? `<div style="width:12px;height:12px;border-radius:50%;background:#4A7CF7;border:2px solid #fff;box-shadow:0 0 18px #4A7CF7ff,0 0 32px #4A7CF788;cursor:pointer"></div>`
        : `<div style="width:10px;height:10px;border-radius:50%;background:#4A7CF7;border:2px solid ${t.dotBorder};box-shadow:0 0 10px #4A7CF7cc,0 0 20px #4A7CF744;cursor:pointer"></div>`,
      iconSize: laxSelected ? [12, 12] : [10, 10],
      iconAnchor: laxSelected ? [6, 6] : [5, 5],
    }));
  }, [laxSelected]);

  // Enriched entries: filter to active, add risk / callsign / connMins
  const enrichedEntries = useMemo(() => {
    const n = new Date();
    const nowMins = n.getHours() * 60 + n.getMinutes();

    return (entries || [])
      .filter(e => {
        if (e.status === "COMPLETED" || e.status === "OFFLOADED") return false;
        return true;
      })
      .map(e => {
        const isCancelled  = e.cancelled || e.atd === "XCL";
        const risk         = getMctRisk(e);
        const connMins     = calcConnTime(e);
        const callsign     = flightToIcaoCallsign((e.inFlt || "").trim());
        const resolved = e.inFlt ? (resolvedCallsigns[e.inFlt.toUpperCase()] || null) : null;
        const callsignAlts = [
          ...paddedCallsigns(callsign),       // zero-padded variants e.g. EVA02
          ...regionalCallsigns(e.inFlt),      // regional operator variants e.g. SKW3456
          ...(resolved ? [resolved] : []),    // icao24-resolved actual callsign e.g. LOT2CA
        ];
        return { ...e, risk, connMins, callsign, callsignAlts, isCancelled };
      });
  }, [entries, resolvedCallsigns]);

  const sortedEntries = useMemo(() => {
    return [...enrichedEntries].sort((a, b) => {
      if (a.isCancelled !== b.isCancelled) return a.isCancelled ? 1 : -1;
      const ta = toMins(a.ata || a.eta || a.sta);
      const tb = toMins(b.ata || b.eta || b.sta);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return ta - tb;
    });
  }, [enrichedEntries]);

  const aircraftByCallsign = useMemo(() => {
    const map = {};
    for (const sv of aircraft) {
      if (sv.callsign) map[sv.callsign] = sv;
    }
    return map;
  }, [aircraft]);

  const liveIds = useMemo(() => {
    const set = new Set();
    for (const entry of enrichedEntries) {
      if (!entry.callsign) continue;
      if (landedEntryIds.has(entry.id)) continue; // already landed — don't re-show as LIVE on next leg
      const sv = aircraftByCallsign[entry.callsign] ?? entry.callsignAlts.map(a => aircraftByCallsign[a]).find(Boolean);
      if (sv) set.add(entry.id);
    }
    return set;
  }, [enrichedEntries, aircraftByCallsign, landedEntryIds]);

  const estIds = useMemo(() => {
    const set = new Set();
    for (const entry of enrichedEntries) {
      if (liveIds.has(entry.id)) continue;
      if (landedAtRef.current[entry.inFlt]) continue;
      if (estimatePosition(entry, now)) set.add(entry.id);
    }
    return set;
  }, [enrichedEntries, liveIds, now]);

  // Effect 1: Leaflet init
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    // eslint-disable-next-line import/no-extraneous-dependencies
    const L = require("leaflet");
    const map = L.map(el, {
      scrollWheelZoom: true,
      zoomControl: false,
      attributionControl: false,
      minZoom: 2,
    });
    const cartoKey = process.env.REACT_APP_CARTO_API_KEY;
    L.tileLayer(`https://{s}.basemaps.cartocdn.com/${isDark ? "dark" : "light"}_nolabels/{z}/{x}/{y}.png${cartoKey ? `?key=${cartoKey}` : ""}`, {
      maxZoom: 10, minZoom: 2, subdomains: "abcd", crossOrigin: true,
    }).addTo(map);
    map.setView([30, -160], 2);
    mapInstanceRef.current = map;

    // Re-run tooltip deconfliction and reposition arc flight-number labels after zoom or pan
    map.on("zoomend moveend", () => {
      deconflictTooltips(map, markersRef);
      for (const { marker, arc, outFlt, dest } of arcLabelsRef.current) {
        const { pos, bothVisible } = getArcLabelState(arc, map);
        marker.setLatLng(pos);
        // Show "UA820/HKG" when destination is off-screen; just "UA820" when both ends visible
        const el = marker.getElement();
        if (el) {
          const inner = el.querySelector("div");
          if (inner) inner.textContent = bothVisible ? outFlt : `${outFlt}/${dest}`;
        }
      }
    });

    // Click on empty map background → deselect all
    map.on("click", () => { setSelectedInFlt(null); setLaxSelected(false); });

    // LAX destination marker — static, added once on init
    const laxDotIcon = () => L.divIcon({
      className: "",
      html: `<div style="width:10px;height:10px;border-radius:50%;background:#4A7CF7;border:2px solid ${t.dotBorder};box-shadow:0 0 10px #4A7CF7cc,0 0 20px #4A7CF744;cursor:pointer"></div>`,
      iconSize: [10, 10],
      iconAnchor: [5, 5],
    });
    const laxDotMarker = L.marker(LAX, { icon: laxDotIcon(), interactive: true, zIndexOffset: 1000 }).addTo(map);
    laxDotMarker.on("click", (e) => {
      L.DomEvent.stopPropagation(e);
      setLaxSelected(prev => !prev);
      setSelectedInFlt(null);
    });
    laxMarkerRef.current = laxDotMarker;

    const laxLabel = L.divIcon({
      className: "",
      html: `<div style="color:${t.laxLabel};font-family:'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif;font-size:14px;font-weight:700;letter-spacing:0.10em;white-space:nowrap;text-shadow:${t.labelShadow}">LAX</div>`,
      iconSize: [0, 0],
      iconAnchor: [-8, -2],
    });
    L.marker(LAX, { icon: laxLabel, interactive: false }).addTo(map);

    // Strip Leaflet's default tooltip and divIcon chrome — our markers are fully inline-styled
    const styleEl = document.createElement("style");
    styleEl.textContent = `
      .leaflet-tooltip {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
      }
      .leaflet-tooltip::before { display: none !important; }
      .leaflet-div-icon {
        background: transparent !important;
        border: none !important;
      }
    `;
    el.appendChild(styleEl);
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Stable callback so markers effect doesn't re-run on every render
  const getMap = useCallback(() => mapInstanceRef.current, []);

  // Effect 2: Aircraft markers (update when enrichedEntries or aircraftByCallsign changes)
  useEffect(() => {
    const map = getMap();
    if (!map) return;
    // eslint-disable-next-line import/no-extraneous-dependencies
    const L = require("leaflet");

    const MAP_CENTER_LON = -160;
    const activeIds = new Set(enrichedEntries.map(e => e.id));

    // Remove stale
    for (const [id, { marker, line, originLine }] of Object.entries(markersRef.current)) {
      if (!activeIds.has(id)) {
        if (marker) map.removeLayer(marker);
        if (line)   map.removeLayer(line);
        if (originLine) map.removeLayer(originLine);
        delete markersRef.current[id];
      }
    }

    // Pre-compute worst-case risk per callsign and inFlt so plane icons reflect
    // the most at-risk connection when multiple connections share the same inbound.
    const worstRiskByCallsign = {};
    const worstRiskByInFlt    = {};
    for (const e of enrichedEntries) {
      const risk = e.isCancelled ? "AT_RISK" : (e.risk || null);
      if (e.callsign) {
        const prev = worstRiskByCallsign[e.callsign];
        if (prev === undefined || RISK_ORDER[risk] < RISK_ORDER[prev]) worstRiskByCallsign[e.callsign] = risk;
      }
      if (e.inFlt) {
        const prev = worstRiskByInFlt[e.inFlt];
        if (prev === undefined || RISK_ORDER[risk] < RISK_ORDER[prev]) worstRiskByInFlt[e.inFlt] = risk;
      }
    }

    // One marker per unique inbound aircraft — multiple connections can share the same inFlt
    const seenCallsigns = new Set();
    for (const entry of enrichedEntries) {
      if (!entry.callsign) continue;
      const sv = aircraftByCallsign[entry.callsign] ?? entry.callsignAlts.map(a => aircraftByCallsign[a]).find(Boolean);
      if (!sv) {
        // Remove stale marker if aircraft no longer tracked
        if (markersRef.current[entry.id]) {
          const { marker, line, originLine } = markersRef.current[entry.id];
          if (marker) map.removeLayer(marker);
          if (line)   map.removeLayer(line);
          if (originLine) map.removeLayer(originLine);
          delete markersRef.current[entry.id];
        }
        continue;
      }
      if (seenCallsigns.has(entry.callsign)) continue; // already have a marker for this aircraft
      seenCallsigns.add(entry.callsign);

      const lat = sv.lat, heading = sv.heading ?? 0;
      let lon = sv.lon;
      if (lat === null || lon === null) continue;

      // Shift into the Pacific-centered world copy (map center -160°) so flights
      // from East Asia (e.g. TPE ~121°E) aren't rendered off-screen to the right.
      while (lon - MAP_CENTER_LON > 180) lon -= 360;
      while (lon - MAP_CENTER_LON < -180) lon += 360;

      const worstRisk = worstRiskByCallsign[entry.callsign] ?? null;
      const color = RISK_COLORS[worstRisk] || RISK_COLORS[null];

      // Great-circle arc from current position → LAX
      const arc = greatCirclePoints(lat, lon, LAX[0], LAX[1], 40);

      // Plane SVG icon
      const planeSvg = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style="display:block;transform:rotate(${heading}deg)">
          <path d="M10,1.5 C11.5,1.5 12,4.5 12,8.5 L19,12 L18.5,13.5 L12,12 L12,16 L15.5,17.5 L15,19 L10,17.5 L5,19 L4.5,17.5 L8,16 L8,12 L1.5,13.5 L1,12 L8,8.5 C8,4.5 8.5,1.5 10,1.5 Z"
            fill="${color}" fill-opacity="0.9" stroke="rgba(0,0,0,0.5)" stroke-width="0.5"/>
        </svg>`;

      const icon = L.divIcon({
        className: "",
        html: planeSvg,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      const altFt    = sv.altM    != null ? Math.round(sv.altM    * 3.28084 / 100) * 100 : null;
      const speedKts = sv.speedMs != null ? sv.speedMs * 1.94384 : null;
      const iataCode = (entry.inAirline || (entry.inFlt || "").slice(0, 2)).toUpperCase();

      // Ground-state label: alt_baro returns "ground" string → altFt is null
      const isOnGround = sv.onGround || altFt === null || altFt <= 50;
      const isNearLAX  = distKm(lat, sv.lon, LAX[0], LAX[1]) <= LAX_RADIUS_KM;
      let groundLabel = null;
      if (isOnGround && isNearLAX) {
        if (speedKts === null || speedKts <= 60) groundLabel = "Taxi in";
        else                                    groundLabel = "Taking off";
      }

      // Hide aircraft on the ground at LAX immediately, and suppress thru-flight
      // re-appearances on their next outbound leg. landedAtRef is sticky — never cleared once set.
      if (isOnGround && isNearLAX) {
        if (!landedAtRef.current[entry.inFlt]) {
          landedAtRef.current[entry.inFlt] = Date.now();
          setLandedEntryIds(prev => {
            if (prev.has(entry.id)) return prev;
            const next = new Set(prev); next.add(entry.id); return next;
          });
        }
        if (markersRef.current[entry.id]) {
          const { marker, line, originLine } = markersRef.current[entry.id];
          if (marker)     map.removeLayer(marker);
          if (line)       map.removeLayer(line);
          if (originLine) map.removeLayer(originLine);
          delete markersRef.current[entry.id];
        }
        continue;
      } else if (landedAtRef.current[entry.inFlt]) {
        // Aircraft was on the ground at LAX but is now airborne on its next leg — suppress it
        if (markersRef.current[entry.id]) {
          const { marker, line, originLine } = markersRef.current[entry.id];
          if (marker)     map.removeLayer(marker);
          if (line)       map.removeLayer(line);
          if (originLine) map.removeLayer(originLine);
          delete markersRef.current[entry.id];
        }
        continue;
      } else if (!isOnGround) {
        // Cold-open detection: Mission Control opened after the thru flight already departed.
        // If ETA is past AND the aircraft is heading away from LAX (not approaching it), suppress.
        const etaMins = toMins(entry.eta || entry.sta);
        const nowMins = nowRef.current.getHours() * 60 + nowRef.current.getMinutes();
        const etaPast = etaMins !== null && nowMins > etaMins + 10;
        const distFromLAX = distKm(lat, sv.lon, LAX[0], LAX[1]);
        // Altitude in ft — null means on ground or no baro data
        const notLowAlt = altFt !== null && altFt > 8000;
        if (etaPast && distFromLAX > 100 && sv.heading != null && notLowAlt) {
          // Bearing FROM LAX TO aircraft — if heading matches this bearing (within 90°), it's flying away.
          // notLowAlt (> 8,000 ft) guards against false positives for aircraft on downwind/approach:
          // downwind at LAX is flown at ~5,000 ft (level), well below a departed thru flight at cruise.
          const bearingAwayFromLAX = bearingDeg(LAX[0], LAX[1], lat, sv.lon);
          const angleDiff = Math.abs(((sv.heading - bearingAwayFromLAX) + 360) % 360);
          const headingAway = angleDiff < 90;
          if (headingAway) {
            if (!landedAtRef.current[entry.inFlt]) {
              landedAtRef.current[entry.inFlt] = Date.now() - 10 * 60 * 1000; // backdate past the 3-min threshold
              setLandedEntryIds(prev => {
                if (prev.has(entry.id)) return prev;
                const next = new Set(prev); next.add(entry.id); return next;
              });
            }
            if (markersRef.current[entry.id]) {
              const { marker, line, originLine } = markersRef.current[entry.id];
              if (marker)     map.removeLayer(marker);
              if (line)       map.removeLayer(line);
              if (originLine) map.removeLayer(originLine);
              delete markersRef.current[entry.id];
            }
            continue;
          }
        }
      }

      // Hide any aircraft still on the ground (e.g. at origin airport, not yet departed)
      if (isOnGround) {
        if (markersRef.current[entry.id]) {
          const { marker, line, originLine } = markersRef.current[entry.id];
          if (marker)     map.removeLayer(marker);
          if (line)       map.removeLayer(line);
          if (originLine) map.removeLayer(originLine);
          delete markersRef.current[entry.id];
        }
        continue;
      }

      // Flight phase label (airborne only)
      let phaseLabel = null;
      if (!isOnGround) {
        const vr = sv.vertRateFpm;
        if (vr != null && vr < -200 && altFt != null && altFt < 10000) phaseLabel = "Final Descent";
        else if (vr != null && vr < -200) phaseLabel = "Descending";
        else if (vr != null && vr >  200) phaseLabel = "Climbing";
        else                              phaseLabel = "Cruising";
      }

      const statusLabel = groundLabel || phaseLabel || "";
      const mono     = "font-family:'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif";
      const ct = isDarkRef.current ? MC_DARK : MC_LIGHT;
      const tooltip  = `
        <div style="background:${ct.tooltipBg};border:1px solid ${color}55;border-radius:8px;padding:6px 10px;${mono};white-space:nowrap;box-shadow:${ct.shadowTooltip};">
          <div style="display:flex;align-items:center;gap:7px">
            <img src="https://www.gstatic.com/flights/airline_logos/70px/${iataCode}.png"
                 style="width:22px;height:22px;border-radius:4px;background:#fff;object-fit:contain;flex-shrink:0;display:block"
                 onerror="this.style.display='none'">
            <div style="display:flex;flex-direction:column;gap:1px">
              <div style="display:flex;align-items:baseline;gap:6px">
                <span style="color:${color};font-weight:700;font-size:11px;letter-spacing:0.07em">${entry.inFlt || entry.callsign}</span>
                ${entry.origin ? `<span style="color:${ct.tooltipMuted};font-size:9px;letter-spacing:0.06em">${entry.origin.trim().toUpperCase()}</span>` : ""}
              </div>
              ${statusLabel ? `<span style="color:${ct.tooltipStatus};font-size:9px">${statusLabel}</span>` : ""}
            </div>
          </div>
        </div>`;

      const originCoords = AIRPORTS[(entry.origin || "").trim().toUpperCase()];
      const makeOriginLine = (toLat, toLon) => originCoords
        ? L.polyline(greatCirclePoints(originCoords[0], originCoords[1], toLat, toLon, 30),
            { color, weight: 1, opacity: 0, dashArray: "3 6" }).addTo(map)
        : null;

      if (markersRef.current[entry.id]) {
        // Update existing
        const { marker, line, originLine: oldOriginLine } = markersRef.current[entry.id];
        marker.setLatLng([lat, lon]);
        marker.setIcon(icon);
        if (line) map.removeLayer(line);
        if (oldOriginLine) map.removeLayer(oldOriginLine);
        const newLine = L.polyline(arc, { color, weight: 1.5, opacity: 0, dashArray: "5 4" }).addTo(map);
        const newOriginLine = makeOriginLine(lat, lon);
        markersRef.current[entry.id] = { marker, line: newLine, originLine: newOriginLine, inFlt: entry.inFlt, tooltipHtml: tooltip, tooltipOpts: { permanent: true, className: "" } };
      } else {
        // Create new
        const marker = L.marker([lat, lon], { icon })
          .bindTooltip(tooltip, { permanent: true, className: "", direction: "top", offset: [0, -16] })
          .addTo(map);
        marker.on("click", () => { setLaxSelected(false); setSelectedInFlt(prev => prev === entry.inFlt ? null : entry.inFlt); });
        marker.on("mouseover", () => {
          setHoveredInFlt(entry.inFlt);
          const ref = markersRef.current[entry.id];
          if (ref?.line) ref.line.setStyle({ opacity: 0.5 });
          if (ref?.originLine) ref.originLine.setStyle({ opacity: 0.25 });
          const oRef = originMarkersRef.current[(entry.origin || "").trim().toUpperCase()];
          if (oRef) { setOriginOpacity(oRef.dotMarker, 1); setOriginOpacity(oRef.labelMarker, 1); }
        });
        marker.on("mouseout", () => {
          setHoveredInFlt(null);
          if (selectedInFltRef.current === entry.inFlt) return;
          if (laxSelectedRef.current) return;
          const ref = markersRef.current[entry.id];
          if (ref?.line) ref.line.setStyle({ opacity: 0 });
          if (ref?.originLine) ref.originLine.setStyle({ opacity: 0 });
        });
        const line = L.polyline(arc, { color, weight: 1.5, opacity: 0, dashArray: "5 4" }).addTo(map);
        const originLine = makeOriginLine(lat, lon);
        markersRef.current[entry.id] = { marker, line, originLine, inFlt: entry.inFlt, tooltipHtml: tooltip, tooltipOpts: { permanent: true, className: "" } };
      }
    }

    // Tag entries and show estimated positions for flights without live data
    // One estimated marker per unique inFlt — avoid stacking identical ghost planes
    const seenEstInFlts = new Set();
    for (const entry of enrichedEntries) {
      if (!liveIds.has(entry.id) && !markersRef.current[entry.id] && !landedAtRef.current[entry.inFlt]) {
        if (seenEstInFlts.has(entry.inFlt)) { entry.hasEst = true; continue; }
        const est = estimatePosition(entry, nowRef.current);
        if (est) {
          seenEstInFlts.add(entry.inFlt);
          const worstRisk = worstRiskByInFlt[entry.inFlt] ?? null;
          const color = RISK_COLORS[worstRisk] || RISK_COLORS[null];

          // Shift arc into the Pacific-centered world copy (map center -160°).
          // Use the arc midpoint (not start) to determine the shift — start-based logic
          // misfires for European origins like WAW (21°E) whose arcs cross the Atlantic
          // westward; the midpoint correctly sits over the Atlantic at ~-48° (no shift),
          // while NRT midpoints sit over the North Pacific at ~191° (-360 shift).
          const rawMidLon = est.arc[Math.floor(est.arc.length / 2)][1];
          let shiftedMid = rawMidLon;
          while (shiftedMid - MAP_CENTER_LON > 180) shiftedMid -= 360;
          while (shiftedMid - MAP_CENTER_LON < -180) shiftedMid += 360;
          const shift = shiftedMid - rawMidLon;
          const arc = shift !== 0
            ? est.arc.map(([lat, lon]) => [lat, lon + shift])
            : est.arc;

          const posIdx = Math.round(est.fractionComplete * (arc.length - 1));
          const markerCoords = arc[Math.min(posIdx, arc.length - 1)];
          const remainingArc = arc.slice(posIdx);

          // Bearing toward LAX along the arc
          const p1 = arc[Math.min(posIdx, arc.length - 2)];
          const p2 = arc[Math.min(posIdx + 3, arc.length - 1)];
          const toRad = d => d * Math.PI / 180;
          const dLon = toRad(p2[1] - p1[1]);
          const y = Math.sin(dLon) * Math.cos(toRad(p2[0]));
          const x = Math.cos(toRad(p1[0])) * Math.sin(toRad(p2[0])) - Math.sin(toRad(p1[0])) * Math.cos(toRad(p2[0])) * Math.cos(dLon);
          const ghostHeading = Math.atan2(y, x) * 180 / Math.PI;

          // Ghost plane icon — same shape as live, outline only, pointed toward LAX
          const ghostIcon = L.divIcon({
            className: "",
            html: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" style="display:block;transform:rotate(${ghostHeading}deg);opacity:0.55">
              <path d="M10,1.5 C11.5,1.5 12,4.5 12,8.5 L19,12 L18.5,13.5 L12,12 L12,16 L15.5,17.5 L15,19 L10,17.5 L5,19 L4.5,17.5 L8,16 L8,12 L1.5,13.5 L1,12 L8,8.5 C8,4.5 8.5,1.5 10,1.5 Z"
                fill="none" stroke="${color}" stroke-width="1.2"/>
            </svg>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
          });

          const mono2 = "font-family:'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif";
          const estIataCode = (entry.inAirline || (entry.inFlt || "").slice(0, 2)).toUpperCase();
          const et = isDarkRef.current ? MC_DARK : MC_LIGHT;
          const tooltip = `
            <div style="background:${et.tooltipBgEst};border:1px solid ${color}44;border-radius:8px;padding:6px 10px;${mono2};white-space:nowrap;box-shadow:${et.shadowTooltip};opacity:0.90">
              <div style="display:flex;align-items:center;gap:7px;margin-bottom:4px">
                <img src="https://www.gstatic.com/flights/airline_logos/70px/${estIataCode}.png"
                     style="width:22px;height:22px;border-radius:4px;background:#fff;object-fit:contain;flex-shrink:0;display:block"
                     onerror="this.style.display='none'">
                <span style="color:${color};font-weight:700;font-size:11px;letter-spacing:0.07em">${entry.inFlt || ""}</span>
                ${entry.origin ? `<span style="color:${et.tooltipMuted};font-size:9px;letter-spacing:0.06em">${entry.origin.trim().toUpperCase()}</span>` : ""}
              </div>
              <div style="color:${et.tooltipMuted};font-size:9px;font-style:italic">Estimated Position</div>
            </div>`;

          const marker = L.marker(markerCoords, { icon: ghostIcon })
            .bindTooltip(tooltip, { permanent: true, className: "", direction: "top", offset: [0, -16] })
            .addTo(map);
          marker.on("click", () => { setLaxSelected(false); setSelectedInFlt(prev => prev === entry.inFlt ? null : entry.inFlt); });
          marker.on("mouseover", () => {
            setHoveredInFlt(entry.inFlt);
            const ref = markersRef.current[entry.id];
            if (ref?.line) ref.line.setStyle({ opacity: 0.3 });
            if (ref?.originLine) ref.originLine.setStyle({ opacity: 0.25 });
            const oRef = originMarkersRef.current[(entry.origin || "").trim().toUpperCase()];
            if (oRef) { setOriginOpacity(oRef.dotMarker, 1); setOriginOpacity(oRef.labelMarker, 1); }
          });
          marker.on("mouseout", () => {
            setHoveredInFlt(null);
            if (selectedInFltRef.current === entry.inFlt) return;
            if (laxSelectedRef.current) return;
            const ref = markersRef.current[entry.id];
            if (ref?.line) ref.line.setStyle({ opacity: 0 });
            if (ref?.originLine) ref.originLine.setStyle({ opacity: 0 });
          });
          const line = remainingArc.length > 1
            ? L.polyline(remainingArc, { color, weight: 1, opacity: 0, dashArray: "3 5" }).addTo(map)
            : null;
          // Flown portion: origin → estimated current position
          const estOriginCoords = AIRPORTS[(entry.origin || "").trim().toUpperCase()];
          const flownArc = arc.slice(0, posIdx + 1);
          const originLine = (estOriginCoords && flownArc.length > 1)
            ? L.polyline(flownArc, { color, weight: 1, opacity: 0, dashArray: "3 6" }).addTo(map)
            : null;
          markersRef.current[entry.id] = { marker, line, originLine, estimated: true, inFlt: entry.inFlt, tooltipHtml: tooltip, tooltipOpts: { permanent: true, className: "" } };
          entry.hasEst = true;
        }
      }
    }

    // Deconflict permanent tooltip directions so nearby labels don't overlap
    deconflictTooltips(map, markersRef);

    // Restore marker opacity, line visibility, and origin airport visibility after every poll refresh.
    // setIcon() resets Leaflet marker opacity to 1, so we must re-apply dimming here.
    if (laxSelectedRef.current) {
      for (const { marker, line, originLine } of Object.values(markersRef.current)) {
        marker.setOpacity(1);
        const tipEl = marker.getTooltip?.()?.getElement?.();
        if (tipEl) tipEl.style.opacity = "1";
        if (line) line.setStyle({ opacity: line.options.dashArray === "3 5" ? 0.3 : 0.5 });
        if (originLine) originLine.setStyle({ opacity: 0.25 });
      }
      for (const { dotMarker, labelMarker } of Object.values(originMarkersRef.current)) {
        setOriginOpacity(dotMarker, 1); setOriginOpacity(labelMarker, 1);
      }
    } else if (selectedInFltRef.current) {
      for (const { marker, line, originLine, inFlt } of Object.values(markersRef.current)) {
        const active = inFlt === selectedInFltRef.current;
        marker.setOpacity(active ? 1 : 0);
        const tipEl = marker.getTooltip?.()?.getElement?.();
        if (tipEl) tipEl.style.opacity = active ? "1" : "0";
        if (active) {
          if (line) line.setStyle({ opacity: line.options.dashArray === "3 5" ? 0.3 : 0.5 });
          if (originLine) originLine.setStyle({ opacity: 0.25 });
        }
      }
      for (const { dotMarker, labelMarker, inFlts } of Object.values(originMarkersRef.current)) {
        const show = inFlts.has(selectedInFltRef.current);
        setOriginOpacity(dotMarker, show ? 1 : 0); setOriginOpacity(labelMarker, show ? 1 : 0);
      }
    }

    // --- Origin airport dots + labels ---
    const activeOrigins = new Map(); // IATA → Set of inFlt
    for (const entry of enrichedEntries) {
      const origin = (entry.origin || "").trim().toUpperCase();
      if (!origin || !AIRPORTS[origin]) continue;
      if (!activeOrigins.has(origin)) activeOrigins.set(origin, new Set());
      activeOrigins.get(origin).add(entry.inFlt);
    }

    // Remove stale
    for (const [code, { dotMarker, labelMarker }] of Object.entries(originMarkersRef.current)) {
      if (!activeOrigins.has(code)) {
        map.removeLayer(dotMarker);
        map.removeLayer(labelMarker);
        delete originMarkersRef.current[code];
      }
    }

    // Add/update
    for (const [code, inFlts] of activeOrigins) {
      if (originMarkersRef.current[code]) {
        const cached = originMarkersRef.current[code];
        // If the marker was detached (e.g. map remount), fall through to recreate
        if (cached.dotMarker.getElement()) {
          cached.inFlts = inFlts;
          continue;
        }
        // Detached — clean up ref and recreate below
        delete originMarkersRef.current[code];
      }
      const [lat, rawLon] = AIRPORTS[code];
      // Shift longitude into the Pacific-centered world copy (map center -160°).
      // Use a 270° threshold (not 180°) so European/Middle-Eastern airports like
      // WAW (21°E) and FRA (8.5°E) stay on the right side of the map rather than
      // getting incorrectly shifted to the far-left world copy at ~-340°.
      // Only East-Asian/Pacific airports (>110°E) need the -360° shift.
      let lon = rawLon;
      while (lon - MAP_CENTER_LON > 270) lon -= 360;
      while (lon - MAP_CENTER_LON < -180) lon += 360;

      const ot = isDarkRef.current ? MC_DARK : MC_LIGHT;
      const dotIcon = L.divIcon({
        className: "",
        html: `<div style="width:7px;height:7px;border-radius:50%;background:${ot.originDot};border:1.5px solid ${ot.dotBorder};box-shadow:0 0 6px ${ot.originDot}cc"></div>`,
        iconSize: [7, 7],
        iconAnchor: [3, 3],
      });
      const labelIcon = L.divIcon({
        className: "",
        html: `<div style="color:${ot.originLabel};font-family:'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif;font-size:13px;font-weight:700;letter-spacing:0.06em;white-space:nowrap;text-shadow:${ot.labelShadow}">${code}</div>`,
        iconSize: [0, 0],
        iconAnchor: [-6, -2],
      });
      const dotMarker   = L.marker([lat, lon], { icon: dotIcon,   interactive: false }).addTo(map);
      const labelMarker = L.marker([lat, lon], { icon: labelIcon, interactive: false }).addTo(map);
      setOriginOpacity(dotMarker,   1);
      setOriginOpacity(labelMarker, 1);
      originMarkersRef.current[code] = { dotMarker, labelMarker, inFlts };
    }
  }, [enrichedEntries, aircraftByCallsign, liveIds, getMap]);

  // Effect 3: Flight-tracking provider chain (OpenSky → adsb.lol → adsb.fi → airplanes.live)
  useEffect(() => {
    if (enrichedEntries.length === 0) return;

    let cancelled = false;

    async function fetchSky() {
      const callsigns = [...new Set(
        enrichedEntries.flatMap(e => [e.callsign, ...e.callsignAlts]).filter(Boolean)
      )];
      if (callsigns.length === 0) return;

      for (let i = 0; i < PROVIDERS.length; i++) {
        const provider = PROVIDERS[i];

        // Skip providers that are in cooldown (e.g. OpenSky after 429)
        const coolUntil = providerCooldownRef.current[provider.key] || 0;
        if (Date.now() < coolUntil) continue;

        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 12000);

        try {
          const vectors = await fetchFromProvider(provider.key, callsigns, ctrl.signal);
          clearTimeout(timer);
          if (cancelled) return;
          setAircraft(vectors);
          setSkyError(false);
          setSkyAge(Date.now());
          setSkyProviderIdx(i);
          return; // success — stop trying
        } catch (err) {
          clearTimeout(timer);
          if (err.rateLimited) {
            providerCooldownRef.current[provider.key] = Date.now() + 5 * 60 * 1000;
            console.warn(`[MissionControl] ${provider.label} rate-limited, cooling 5 min`);
          } else {
            providerCooldownRef.current[provider.key] = Date.now() + 2 * 60 * 1000;
            console.warn(`[MissionControl] ${provider.label} failed (${err.message}), cooling 2 min`);
          }
        }
      }

      // All providers exhausted
      if (!cancelled) setSkyError(true);
    }

    fetchSky();
    const interval = setInterval(fetchSky, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enrichedEntries]); // eslint-disable-line react-hooks/exhaustive-deps -- providerCooldownRef is a ref, not reactive


  // Effect 3b: ICAO24 fallback for EU/partner carriers with non-standard callsigns.
  // Uses icao24AttemptedRef to run the AviationStack call exactly once per flight
  // per session regardless of how many times enrichedEntries re-renders.
  useEffect(() => {
    // Flights that haven't been attempted yet, aren't already resolved,
    // and aren't already tracked live by the main poll via their primary callsign.
    // The last check prevents AviationStack from re-resolving flights that the
    // main poll already finds correctly, which could corrupt their callsign mapping.
    const toProcess = [...new Set(
      enrichedEntries
        .filter(e =>
          e.inFlt &&
          !e.inFlt.toUpperCase().startsWith("UA") &&
          !resolvedCallsigns[e.inFlt.toUpperCase()] &&
          !icao24AttemptedRef.current.has(e.inFlt.toUpperCase()) &&
          !aircraftByCallsign[e.callsign]  // skip if primary callsign already matched
        )
        .map(e => e.inFlt.toUpperCase())
    )];

    if (toProcess.length === 0) return;

    // Mark immediately — prevents duplicate calls if enrichedEntries changes
    // while the async work is in progress.
    toProcess.forEach(flt => icao24AttemptedRef.current.add(flt));

    let active = true;

    (async () => {
      // Step 1: get icao24 for each flight (cache or AviationStack)
      const toFetch = []; // { flt, icao24 }[]
      for (const flt of toProcess) {
        if (!active) return;
        const cached = getCachedIcao24(flt);
        if (cached?.icao24) {
          toFetch.push({ flt, icao24: cached.icao24 });
          continue;
        }
        try {
          const result = await lookupCallsign(flt);
          if (result.icao24) {
            setCachedIcao24(flt, result);
            toFetch.push({ flt, icao24: result.icao24 });
            console.log(`[MC] AviationStack ${flt} → icao24 ${result.icao24}`);
          } else {
            // Not in AviationStack — allow retry next session by removing from ref
            icao24AttemptedRef.current.delete(flt);
          }
        } catch (err) {
          console.warn(`[MC] AviationStack ${flt}:`, err.message);
          icao24AttemptedRef.current.delete(flt); // retry on next effect run
        }
      }

      if (toFetch.length === 0 || !active) return;

      // Step 2: find actual broadcast callsign via adsb-proxy icao24 lookup
      const icao24s = [...new Set(toFetch.map(f => f.icao24))];
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 12000);
        const vectors = await fetchFromProxy([], ctrl.signal, icao24s);
        clearTimeout(timer);
        if (!active) return;

        const newResolved = {};
        for (const v of vectors) {
          if (!v.callsign) continue;
          for (const { flt, icao24 } of toFetch) {
            if ((getCachedIcao24(flt)?.icao24 ?? "") === icao24) {
              newResolved[flt] = v.callsign;
              const c = getCachedIcao24(flt);
              setCachedIcao24(flt, { ...c, resolvedCallsign: v.callsign });
              console.log(`[MC] ${flt} → callsign ${v.callsign}`);
              break;
            }
          }
        }

        if (Object.keys(newResolved).length > 0) {
          setResolvedCallsigns(prev => ({ ...prev, ...newResolved }));
        }

        // Flights with icao24 but not found in ADS-B (out of range): allow retry
        const resolved = new Set(Object.keys(newResolved));
        toFetch.filter(f => !resolved.has(f.flt))
               .forEach(f => icao24AttemptedRef.current.delete(f.flt));
      } catch (err) {
        console.warn("[MC] icao24 proxy:", err.message);
        toFetch.forEach(f => icao24AttemptedRef.current.delete(f.flt));
      }
    })();

    return () => { active = false; };
  }, [enrichedEntries, aircraftByCallsign]); // eslint-disable-line react-hooks/exhaustive-deps -- icao24AttemptedRef guards against re-processing; resolvedCallsigns excluded to avoid loop

  // Effect 4: draw all outbound arcs for the selected inbound flight
  useEffect(() => {
    const map = getMap();
    if (!map) return;
    const L = require("leaflet");

    if (selectedArcRef.current) {
      map.removeLayer(selectedArcRef.current);
      selectedArcRef.current = null;
    }
    arcLabelsRef.current = [];

    const activeInFlt = selectedInFlt || hoveredInFlt;
    if (!activeInFlt && !laxSelected) return;

    const _now = new Date();
    const nowMins = _now.getHours() * 60 + _now.getMinutes();

    // All entries to draw outbound arcs for
    const group = laxSelected
      ? sortedEntries
      : sortedEntries.filter(e => e.inFlt === activeInFlt);
    if (group.length === 0) return;

    // For thru-flights the same outFlt appears with multiple dest values (e.g. UA820→HKG and
    // UA820→BKK).  We want to draw only the closest stop to LAX (HKG), not the final destination.
    // Pre-scan the group and, per outFlt, record the entry whose dest is nearest to LAX.
    const winnerByOutFlt = new Map(); // outFlt → { entry, dist }
    for (const entry of group) {
      const outFlt = (entry.outFlt || "").toUpperCase();
      if (!outFlt) continue;
      const coords = AIRPORTS[(entry.dest || "").toUpperCase()];
      if (!coords) continue;
      const d = distKm(LAX[0], LAX[1], coords[0], coords[1]);
      const prev = winnerByOutFlt.get(outFlt);
      if (!prev || d < prev.dist) winnerByOutFlt.set(outFlt, { entry, dist: d });
    }

    const layers = [];
    const seenDests = new Set();

    for (const entry of group) {
      const dest    = (entry.dest   || "").toUpperCase();
      const outFlt  = (entry.outFlt || "").toUpperCase();
      if (!dest || seenDests.has(dest)) continue;
      // Thru-flight guard: skip stops that aren't the closest to LAX for this outFlt
      if (outFlt && winnerByOutFlt.has(outFlt) && winnerByOutFlt.get(outFlt).entry !== entry) continue;
      const destCoords = AIRPORTS[dest];
      if (!destCoords) continue;
      seenDests.add(dest);

      const isCancelledEntry = entry.isCancelled;
      const arcColor   = isCancelledEntry ? "#D46060" : (RISK_COLORS[entry.risk] || RISK_COLORS[null]);
      const arcWeight  = isCancelledEntry ? 2 : 2;
      const arcOpacity = isCancelledEntry ? 0.65 : (laxSelected ? 0.5 : selectedInFlt ? 0.75 : 0.45);
      const baseArc = greatCirclePoints(LAX[0], LAX[1], destCoords[0], destCoords[1], 160);
      const arc = isCancelledEntry ? zigzagPoints(baseArc, 0.1) : baseArc;
      // Use the arc's endpoint for marker placement — greatCirclePoints unwraps longitudes
      // so trans-Pacific routes end at e.g. -220° instead of canonical +140°.
      // The map is centered at -160° (Pacific), so canonical Asian coords (+140°) would
      // land ~300° off-screen to the right. The arc endpoint is already in the correct world copy.
      const destPos = baseArc[baseArc.length - 1];

      layers.push(L.polyline(arc, { color: arcColor, weight: arcWeight, opacity: arcOpacity }));

      const mono = "font-family:'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif";
      const at = isDarkRef.current ? MC_DARK : MC_LIGHT;

      // Destination dot + IATA label
      const destLabel = L.divIcon({
        className: "",
        html: `<div style="color:${arcColor};${mono};font-size:13px;font-weight:700;white-space:nowrap;text-shadow:${at.labelShadow};padding-left:10px">${dest}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      layers.push(L.marker(destPos, { icon: destLabel }));

      const dot = L.divIcon({
        className: "",
        html: `<div style="width:8px;height:8px;border-radius:50%;background:${arcColor};border:2px solid ${at.dotBorder};box-shadow:0 0 6px ${arcColor}"></div>`,
        iconSize: [8, 8],
        iconAnchor: [4, 4],
      });
      layers.push(L.marker(destPos, { icon: dot }));

      // Flight number label — positioned at the midpoint of the currently-visible arc segment.
      // Shows "UA820/HKG" when the destination is off-screen, "UA820" when both ends are visible.
      // Uses baseArc (not zigzag arc) for smooth placement.
      if (entry.outFlt) {
        const { pos: labelPos, bothVisible } = getArcLabelState(baseArc, map);
        const labelText = bothVisible ? entry.outFlt : `${entry.outFlt}/${dest}`;
        const midLabel = L.divIcon({
          className: "",
          html: `<div style="color:${arcColor};${mono};font-size:13px;font-weight:700;white-space:nowrap;letter-spacing:0.05em;transform:translate(-50%,-50%);text-shadow:${at.labelShadow}">${labelText}</div>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        });
        const midMarker = L.marker(labelPos, { icon: midLabel, interactive: false });
        layers.push(midMarker);
        arcLabelsRef.current.push({ marker: midMarker, arc: baseArc, outFlt: entry.outFlt, dest });
      }
    }

    // Gray dashed lines: origin → LAX for landed inbound flights
    const seenOrigins = new Set();
    for (const entry of group) {
      const origin = (entry.origin || "").toUpperCase().trim();
      if (!origin || seenOrigins.has(origin)) continue;
      const originCoords = AIRPORTS[origin];
      if (!originCoords) continue;
      // Only draw if the inbound has landed or its ETA/STA has passed
      const arrMins = toMins(entry.ata || entry.eta || entry.sta);
      if (arrMins === null) continue;
      let minsToArr = arrMins - nowMins;
      if (minsToArr < -600) minsToArr += 1440;
      if (minsToArr > 0) continue;
      seenOrigins.add(origin);
      const originArc = greatCirclePoints(originCoords[0], originCoords[1], LAX[0], LAX[1], 60);
      layers.push(L.polyline(originArc, { color: "#666", weight: 1.5, opacity: 0.4, dashArray: "4 7" }));
    }

    if (layers.length === 0) return;
    selectedArcRef.current = L.layerGroup(layers).addTo(map);

    return () => {
      if (selectedArcRef.current) {
        map.removeLayer(selectedArcRef.current);
        selectedArcRef.current = null;
      }
    };
  }, [selectedInFlt, hoveredInFlt, laxSelected, sortedEntries, getMap]);

  // Effect 5: dim/restore markers, data tags, inbound arcs, and origin labels based on selection
  useEffect(() => {
    for (const [, { marker, line, originLine, inFlt }] of Object.entries(markersRef.current)) {
      const active = laxSelected || !selectedInFlt || inFlt === selectedInFlt;
      marker.setOpacity(active ? 1 : 0);
      const tipEl = marker.getTooltip?.()?.getElement?.();
      if (tipEl) tipEl.style.opacity = active ? "1" : "0";
      const showLine = laxSelected || (active && (selectedInFlt || hoveredInFlt === inFlt));
      if (line) line.setStyle({ opacity: showLine ? (line.options.dashArray === "3 5" ? 0.3 : 0.5) : 0 });
      if (originLine) originLine.setStyle({ opacity: showLine ? 0.25 : 0 });
    }
  }, [selectedInFlt, laxSelected]);

  // Hide/show origin airport markers whenever selection changes.
  // The poll effect only runs on data refresh, so deselecting wouldn't clear them otherwise.
  useEffect(() => {
    for (const { dotMarker, labelMarker, inFlts } of Object.values(originMarkersRef.current)) {
      const show = true; // always visible
      setOriginOpacity(dotMarker,   show ? 1 : 0);
      setOriginOpacity(labelMarker, show ? 1 : 0);
    }
  }, [selectedInFlt, laxSelected]);

  // Card hover callbacks — mirror the map marker mouseover/mouseout logic imperatively
  const handleCardEnter = useCallback((entry) => {
    setHoveredInFlt(entry.inFlt);
    const ref = Object.values(markersRef.current).find(r => r.inFlt === entry.inFlt);
    if (ref?.line)       ref.line.setStyle({ opacity: 0.5 });
    if (ref?.originLine) ref.originLine.setStyle({ opacity: 0.25 });
    const oRef = originMarkersRef.current[(entry.origin || "").trim().toUpperCase()];
    if (oRef) { setOriginOpacity(oRef.dotMarker, 1); setOriginOpacity(oRef.labelMarker, 1); }
  }, []);

  const handleCardLeave = useCallback((entry) => {
    setHoveredInFlt(null);
    if (selectedInFltRef.current === entry.inFlt) return;
    if (laxSelectedRef.current) return;
    const ref = Object.values(markersRef.current).find(r => r.inFlt === entry.inFlt);
    if (ref?.line)       ref.line.setStyle({ opacity: 0 });
    if (ref?.originLine) ref.originLine.setStyle({ opacity: 0 });
  }, []);

  // Stats for TopBar
  const atRisk = sortedEntries.filter(e => e.risk === "AT_RISK").length;
  const tight  = sortedEntries.filter(e => e.risk === "TIGHT").length;
  const safe   = sortedEntries.filter(e => e.risk === "SAFE").length;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: t.pageBg, overflow: "hidden",
    }}>
      {/* Map fills entire background */}
      <div
        ref={mapContainerRef}
        style={{ position: "absolute", inset: 0, zIndex: 0 }}
      />

      {/* Floating TopBar */}
      <TopBar
        now={now}
        atRisk={atRisk}
        tight={tight}
        safe={safe}
        skyError={skyError}
        skyProviderIdx={skyProviderIdx}
        skyAge={skyAge}
        onClose={onClose}
        t={t}
      />

      {/* Panel toggle button */}
      <button
        onClick={() => setPanelVisible(v => !v)}
        title={panelVisible ? "Hide connections panel" : "Show connections panel"}
        style={{
          position: "absolute", top: 16, left: 16, zIndex: 11,
          background: t.panelBg, backdropFilter: "blur(12px)",
          border: `1px solid ${t.border}`, borderRadius: "50%",
          width: 36, height: 36, cursor: "pointer", color: t.textPrimary,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: t.shadowPanel, flexShrink: 0,
        }}
      >
        {panelVisible ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="1" y="1" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/>
            <line x1="5" y1="1" x2="5" y2="13" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M3 5.5L1.5 7L3 8.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="1" y="1" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/>
            <line x1="5" y1="1" x2="5" y2="13" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M7 5.5L8.5 7L7 8.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>

      {/* Floating LeftPanel */}
      {panelVisible && (
        <LeftPanel
          entries={sortedEntries}
          now={now}
          selectedInFlt={selectedInFlt}
          hoveredInFlt={hoveredInFlt}
          onSelect={setSelectedInFlt}
          onCardEnter={handleCardEnter}
          onCardLeave={handleCardLeave}
          liveIds={liveIds}
          estIds={estIds}
          t={t}
        />
      )}
    </div>
  );
}
